import { Button, Chip, Grid, IconButton, Tooltip } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import Box from '@mui/material/Box';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from "../../utils/api.types";
import { PermissionsEnum } from "../../utils/auth.types";
import CustomDataGrid from '../DataGrid/DataGrid';

import { fetchInventories } from '../../store/inventoryStore/inventoryThunk';
import { Inventory } from '../../utils/inventory.types';

import RemoveRedEyeTwoToneIcon from '@mui/icons-material/RemoveRedEyeTwoTone';
import { useNavigate } from "react-router-dom";
import { Utils } from '../../utils/common-utils';
import InventoryFilters from '../Filters/InventoryFilters';

interface InventoryList {
    accountId?: string;
    inventoryId?: string;
}
const InventoryListData: FC<InventoryList> = (props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { inventories, status, rowCount } = useAppSelector(state => state.inventories);
    const { permissions, selectedAccount } = useAppSelector(state => state.auth);

    const [open, setOpen] = useState(false);
    const [inventoryId, setInventory] = useState('');
    const [fetchData, setFetchData] = useState('');
    const canUpdateAccount = permissions?.includes(PermissionsEnum.Account_Update);

    const [filters, setFilters] = useState('');
    const [isRefersh, setIsRefersh] = useState(false);

    const [page, setPage] = useState(0); // Current page (0-based index)
    const [pageSize, setPageSize] = useState(apiLimit.limit); // Rows per page
    const [pageQueryString, setPageQueryString] = useState(``); // Current page (0-based index)
    const [pageSizeQueryString, setPageSizeQueryString] = useState(`&limit=${apiLimit.limit}`); // Rows per page

    /**
    * Handler for account name click.
    */
    const handleEditInventoryClick = useCallback(({ _id }: Inventory) => {
        //Call edit function
        let navLink = `/inventory/edit/${_id}`;
        navigate(navLink);

    }, []);

    /**
    * Handler for New Inventory button click.
    */
    const handleAddInventory = () => {
        const navLink = `/inventory/create`;
        navigate(navLink);
    };

    /**
      * Fetch all the inventories on page load.
      */
    useEffect(() => {
        const { _id: id } = selectedAccount;
        const params = filters ? filters + pageQueryString + pageSizeQueryString : pageQueryString + pageSizeQueryString;
        dispatch(fetchInventories({ accountId: id, type: '', params }));
        setIsRefersh(true);
    }, [dispatch, selectedAccount, filters, isRefersh, page, pageSize, pageQueryString, pageSizeQueryString]);

    //Check status     
    const checkStatus = useCallback((value) => {
        return (value == 'Active') ? <Chip size='small' style={{ borderRadius: 7, color: '#49a563', backgroundColor: '#edf6ef' }} label={`${value}`} />
            : <Chip size='small' style={{ borderRadius: 7, color: '#c33b3b', backgroundColor: '#fbecec' }} label={`${value}`} />
    }, []);


    /** 
        * Column defs for account list data grid.
    */
    const columns: GridColDef[] = useMemo(() => [

        {
            field: "name",
            headerName: 'Name',
            type: "string",
            flex: 2,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (
                    <p onClick={() => navigate(`/inventory/view/${row._id}`)} style={{ color: '#488dff' }}>
                        <>
                            {/* <Tooltip title={`${row.name ? row.name : ''}`}>{(row.name.length > 30) ? `${row.name.substring(0, 30)}...` : row.name}</Tooltip><br /> */}
                            {/* {(row.name.length > 30) ? `${row.name.substring(0, 30)}...` : row.name}<br/> */}
                            <Tooltip title={`${row.name}`}>
                                <span style={{ color: '#488dff' }}>
                                    {(row.name.length > 30) ? `${row.name.substring(0, 30)} ...` : row.name}
                                </span>
                            </Tooltip> <br />
                            <Tooltip title={`${row?.description || ''}`}>
                                <span style={{ color: '#464646' }}>
                                    {(row?.description?.length > 25) ? `${row?.description?.substring(0, 25) || ''} ...` : row?.description || ''}
                                </span>
                            </Tooltip>
                        </>
                    </p>
                )
            }
        },
        {
            field: "hsnCode",
            headerName: 'HSN/SAC',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>{row.hsnCode}
                </>
                )
            }
        },
        {
            field: "gst",
            headerName: 'GST',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    {row.gst && <>
                        @{row.gst.value || ''}%</>
                    }

                    {/* <Tooltip title={row.gst.lable}><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip> */}

                </>
            )
        },
        {
            field: "MRP",
            headerName: 'MRP',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {Utils.formatNumber(row.mrp || 0, true, true)}
                </>);
            },
        },
        {
            field: "purchasePrice",
            headerName: 'Pur. Price',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {(row.purchase && row.purchase.price && row.stock.stockAsOfDate != undefined) ?
                        <>{Utils.formatNumber(row.purchase.price || 0, true, true)}</>
                        : "-"
                    }

                </>);
            },
        },
        {
            field: "salesPrice",
            headerName: 'Sale Price',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {(row.sale && row.sale.price && row.sale.unit && row.sale.unit != undefined) ?
                        <>{Utils.formatNumber(row.sale.price || 0, true, true)} / {row.sale.unit.code}</>
                        : "-"
                    }

                </>);
            },
        },
        {
            field: "currentStock",
            headerName: 'In Stock',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    {(row.sale && row.sale.unit && row.sale.unit.code && row.stock && row.stock.currentStock) ?
                        <Chip size='small' style={{ borderRadius: 7, color: '#488dff', backgroundColor: '#f1f0fc' }} label={`${Utils.formatNumber(row.stock.currentStock, false)} ${row.sale.unit.code}`} />
                        :
                        <Chip size='small' style={{ borderRadius: 7, color: '#c33b3b', backgroundColor: '#fbecec' }} label={`Out of Stock`} />
                    }
                    {/* <br/>
                {(row.stock.currentStock > 0 && row.stock.stockAsOfDate && row.stock.stockAsOfDate != '' && row.stock.stockAsOfDate != undefined) ?
                    <>{moment(row.stock.stockAsOfDate).format("DD/MM/YYYY")}</>
                    : '' 
                } */}
                </>
            )
        },
        {
            field: "status",
            headerName: 'Status',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>{checkStatus(value)}</>
            )
        },
        // {
        //     field: "action",
        //     headerName: 'Action',
        //     type: "string",
        //     width:130,
        //     renderCell: ({ row, value }: GridValueGetterParams) => {
        //         return (
        //             <CustomizedEstimateMenus type={props.type} row={row} refreshCompOnClose={isRefersh}/>
        //         );
        //     }
        // }

        {
            field: "actions",
            headerName: 'Actions',
            type: "actions",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    <IconButton
                        style={{ color: '#488dff', backgroundColor: '#fff', border: '0.1px solid #488dff', height: 35, width: 35, marginRight: 10 }}
                        size='small' onClick={() => navigate(`/inventory/view/${row._id}`)}><RemoveRedEyeTwoToneIcon style={{ fontSize: 16 }} />

                    </IconButton>
                    <IconButton
                        style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size='small' onClick={() => handleEditInventoryClick(row)}><EditIcon style={{ fontSize: 16 }} />

                    </IconButton>
                </>
            )
            // getActions: (params: GridValueGetterParams) => {
            //     const editAction = (
            //         <GridActionsCellItem
            //             icon={<EditIcon/>}
            //             title="Edit"
            //             label="Edit"
            //             style={{marginLeft:-10, marginRight: '3px', color:'#488dff',backgroundColor:'#f1f0fc',border:'1px solid #488dff',borderRadius:10 }}
            //             onClick={() => handleEditInventoryClick(params.row)} />
            //     );

            //     const deleteAction = (
            //     <GridActionsCellItem
            //         icon={<DeleteIcon/>}
            //         title="Delete"
            //         style={{ marginRight: '0px', color:'#c33b3b',backgroundColor:'#fbecec',border:'1px solid #c33b3b',borderRadius:10 }}
            //         label="Delete"
            //         disabled
            //         // onClick={deleteSelectedAddress(params.row)}
            //     />
            //     );

            //     const actions: JSX.Element[] = [];

            //     // Push edit action if user has update permission.
            //     if (canUpdateAccount) {
            //     actions.push(editAction);
            //     }
            //     // Push edit action if user has delete permission.
            //     if (true) {
            //     actions.push(deleteAction);
            //     }
            //     return actions;
            // }
        }
    ], [
        // deleteSelected,
        // editSelected,
        //canUpdateAccount,
        // canDeleteAccount,
        //handleNameClick
    ]);

    const filterOnChangeHandler = (data: any) => {
        console.log("filter date from inventory listing ===== ", data);
        setFilters(data);
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setPageQueryString(`&page=${newPage}`);
        setPage(newPage);
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSizeQueryString(`&limit=${newPageSize}`);
        setPageSize(newPageSize);
    };

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", width: '100%', pt: 2, pb: 1, gap: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: '100%' }}>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <InventoryFilters
                            type={'sales'}
                            filterHandler={filterOnChangeHandler} />
                    </Box>
                    <div style={{ marginLeft: 10 }}>
                        <Button
                            startIcon={<AddIcon />}
                            size='small'
                            onClick={handleAddInventory}
                            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                            variant="contained"
                        >
                            New Inventory
                        </Button>
                    </div>
                </Box>
            </Box>

            {inventories && inventories.length > 0 &&
                <div style={{ height: "800px", width: "100%", marginTop: '0px', overflow: 'auto' }}>
                    <CustomDataGrid
                        columns={columns}
                        rows={inventories}
                        rowCount={rowCount}
                        rowsPerPageOptions={defaultRowsPerPageOptions}
                        page={page}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        onPageChange={handlePageChange}
                        checkboxSelection={true}
                        getRowId={(row) => row._id || Math.random().toString()}
                        loading={status === ApiStatus.LOADING} />
                </div>
            }
        </>
    );
}
export default InventoryListData;